import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  Alert,
  Popover,
  Chip,
  Stack
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useGetCategoryList } from "../../hooks/api/useCategory";
import AddCategoryModal from "./AddCategoryModal";
import EditCategoryModal from "./EditCategoryModal";
import DeleteCategoryModal from "./DeleteCategoryModal";
import NoCategoryFound from "./NoCategoryFound";
import ViewCategoryDetail from "./ViewCategoryDetail";
import ViewSubcategoryDetail from "./ViewSubcategoryDetail";
import { Category } from "./types/categoryTypes";
import { buildCategoryTree } from "./utils/categoryUtils";
import ExportData from "../common/ExportData/ExportData";
import { exportCategoryColumns } from "../common/constants/exportColumns";
import { CATEGORY_STATUS } from "./enums/categoryEnums";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import CategoryListToolbar from "./CategoryListToolbar";
import CategoryListFilters from "./CategoryListFilters";
import CategoryListTable from "./CategoryListTable";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";

import { LOG_LEVEL, buildLogContext, sendAppLog } from "../../Utils/appLogger";

type Order = "asc" | "desc";

const CategoryList: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { categoryList } = useSelector((state: RootState) => state.categories);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const getCategoryListMutation = useGetCategoryList();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const [search, setSearch] = useState("");
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof Category>("name");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [depthFilter, setDepthFilter] = useState<string>("all");
  const [hasProductsFilter, setHasProductsFilter] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [viewMode, setViewMode] = useState<
    "list" | "category-detail" | "subcategory-detail"
  >("list");
  const [selectedCategoryForView, setSelectedCategoryForView] =
    useState<Category | null>(null);
  const [selectedParentCategory, setSelectedParentCategory] =
    useState<Category | null>(null);

  const filtersOpen = Boolean(filtersAnchorEl);

  useEffect(() => {
    if (!userDetails?.companyId) return;
    const promise = getCategoryListMutation.mutateAsync({
      status: CATEGORY_STATUS.ACTIVE
    });
    if (promise && typeof promise.catch === "function") {
      promise.catch((error: unknown) =>
        console.error("Failed to fetch categories:", error)
      );
    }
  }, [userDetails?.companyId]);

  const refetchCategories = useCallback(() => {
    return getCategoryListMutation.mutateAsync({
      status: CATEGORY_STATUS.ACTIVE
    });
  }, [getCategoryListMutation]);

  const filteredList = useMemo(() => {
    let result = categoryList ?? [];
    if (search.trim()) {
      result = result.filter((c) =>
        Object.values(c).join(" ").toLowerCase().includes(search.toLowerCase())
      );
    }
    if (statusFilter !== "all") {
      result = result.filter(
        (c) => c.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }
    if (depthFilter !== "all") {
      result = result.filter((c) => String(c.depth) === depthFilter);
    }
    if (hasProductsFilter) {
      result = result.filter((c) => (c.productCount || 0) > 0);
    }
    return result;
  }, [categoryList, search, statusFilter, depthFilter, hasProductsFilter]);

  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      const aVal = a[orderBy] ?? "";
      const bVal = b[orderBy] ?? "";
      return order === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredList, order, orderBy]);

  const categoryTree = useMemo(
    () => buildCategoryTree(sortedList),
    [sortedList]
  );

  const uniqueDepths = useMemo(() => {
    return Array.from(new Set((categoryList ?? []).map((c) => c.depth))).sort(
      (a, b) => a - b
    );
  }, [categoryList]);

  const handleSort = useCallback(
    (property: keyof Category) => {
      setOrderBy(property);
      setOrder((prevOrder) => {
        if (orderBy !== property) return "asc";
        return prevOrder === "asc" ? "desc" : "asc";
      });
    },
    [orderBy]
  );

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedCategories(filteredList.map((c) => c.id));
      } else {
        setSelectedCategories([]);
      }
    },
    [filteredList]
  );

  const handleSelectOne = useCallback((categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  const clearFilters = useCallback(() => {
    setSearch("");
    setStatusFilter("all");
    setDepthFilter("all");
    setHasProductsFilter(false);
  }, []);

  const closeFiltersPopover = useCallback(() => {
    setFiltersAnchorEl(null);
  }, []);

  const activeFiltersCount = [
    search.trim() !== "",
    statusFilter !== "all",
    depthFilter !== "all",
    hasProductsFilter
  ].filter(Boolean).length;

  // View: category detail
  if (viewMode === "category-detail" && selectedCategoryForView) {
    return (
      <>
        <ViewCategoryDetail
          category={selectedCategoryForView}
          onBack={() => {
            setViewMode("list");
            setSelectedCategoryForView(null);
          }}
          onViewSubcategory={(subcat) => {
            setSelectedParentCategory(selectedCategoryForView);
            setSelectedCategoryForView(subcat);
            setViewMode("subcategory-detail");
          }}
          onEdit={(cat) => {
            setSelectedCategory(cat);
            setOpenEditModal(true);
          }}
          onDelete={(cat) => {
            setSelectedCategory(cat);
            setOpenDeleteModal(true);
          }}
          onRefetchCategoryList={refetchCategories}
        />
        {selectedCategory && (
          <>
            <EditCategoryModal
              open={openEditModal}
              onClose={() => {
                setOpenEditModal(false);
                setSelectedCategory(null);
              }}
              category={selectedCategory}
              onSuccess={refetchCategories}
            />
            <DeleteCategoryModal
              open={openDeleteModal}
              onClose={() => {
                setOpenDeleteModal(false);
                setSelectedCategory(null);
              }}
              category={selectedCategory}
            />
          </>
        )}
      </>
    );
  }

  // View: subcategory detail
  if (
    viewMode === "subcategory-detail" &&
    selectedCategoryForView &&
    selectedParentCategory
  ) {
    return (
      <>
        <ViewSubcategoryDetail
          subcategory={selectedCategoryForView}
          parentCategory={selectedParentCategory}
          onBack={() => {
            setSelectedCategoryForView(selectedParentCategory);
            setSelectedParentCategory(null);
            setViewMode("category-detail");
          }}
          onEdit={(cat) => {
            setSelectedCategory(cat);
            setOpenEditModal(true);
          }}
          onDelete={(cat) => {
            setSelectedCategory(cat);
            setOpenDeleteModal(true);
          }}
        />
        {selectedCategory && (
          <>
            <EditCategoryModal
              open={openEditModal}
              onClose={() => {
                setOpenEditModal(false);
                setSelectedCategory(null);
              }}
              category={selectedCategory}
              onSuccess={refetchCategories}
            />
            <DeleteCategoryModal
              open={openDeleteModal}
              onClose={() => {
                setOpenDeleteModal(false);
                setSelectedCategory(null);
              }}
              category={selectedCategory}
            />
          </>
        )}
      </>
    );
  }

  // Loading
  if (getCategoryListMutation.isPending) {
    return (
      <CommonLoader
        loadingMessage={t("loading-categories", "Loading categories...")}
      />
    );
  }

  // Error
  if (getCategoryListMutation.isError) {
    return (
      <Box sx={{ minHeight: "calc(100vh - 110px)", p: 3, pt: 2 }}>
        <Alert severity="error">
          {t(
            "failed-to-load-categories",
            "Failed to load categories. Please try again later."
          )}
        </Alert>
      </Box>
    );
  }

  // Empty (no categories at all)
  if (!categoryList || categoryList.length === 0) {
    return (
      <>
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          sx={{ minHeight: "calc(100vh - 110px)", pt: 2, mt: 1 }}
        >
          <NoCategoryFound onAddClick={() => setOpenAddModal(true)} />
        </Box>
        <AddCategoryModal
          open={openAddModal}
          onClose={() => setOpenAddModal(false)}
          onSuccess={refetchCategories}
        />
      </>
    );
  }

  // No results after filtering
  if (filteredList.length === 0) {
    return (
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        sx={{
          minHeight: "calc(100vh - 110px)",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          pt: 2,
          mt: 1
        }}
      >
        <CategoryListToolbar
          onOpenFilters={(e) => setFiltersAnchorEl(e.currentTarget)}
          onAddCategory={() => setOpenAddModal(true)}
          isMobile={matchMD}
          filtersActiveCount={activeFiltersCount}
        />
        <Popover
          open={filtersOpen}
          anchorEl={filtersAnchorEl}
          onClose={closeFiltersPopover}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                mt: 1.5,
                minWidth: 320,
                maxWidth: 400,
                maxHeight: "85vh",
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                boxShadow: "none"
              }
            }
          }}
        >
          <CategoryListFilters
            search={search}
            onSearchChange={setSearch}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            depthFilter={depthFilter}
            onDepthFilterChange={setDepthFilter}
            hasProductsFilter={hasProductsFilter}
            onHasProductsFilterChange={setHasProductsFilter}
            uniqueDepths={uniqueDepths}
            onClear={clearFilters}
            activeCount={activeFiltersCount}
          />
        </Popover>
        <Alert
          severity="info"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={clearFilters}>
              {t("clear-filters", "Clear filters")}
            </Button>
          }
        >
          {t(
            "no-categories-matching-filters",
            "No categories found matching your filters."
          )}
        </Alert>
        <AddCategoryModal
          open={openAddModal}
          onClose={() => setOpenAddModal(false)}
          onSuccess={refetchCategories}
        />
      </Box>
    );
  }

  // Main list view
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.06, delayChildren: 0.1 }
    }
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      sx={{
        minHeight: "calc(100vh - 110px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "100%",
        maxWidth: "100%",
        padding: 0,
        pt: 2,
        color: "text.primary",
        mt: 1
      }}
    >
      <Box
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        sx={{ display: "flex", flexDirection: "column", gap: 3 }}
      >
        <Box component={motion.div} variants={itemVariants}>
          <CategoryListToolbar
            title={t("category-list", "Category list")}
            onOpenFilters={(e) => setFiltersAnchorEl(e.currentTarget)}
            onAddCategory={() => setOpenAddModal(true)}
            isMobile={matchMD}
            filtersActiveCount={activeFiltersCount}
          >
            {selectedCategories.length > 0 && (
              <Chip
                label={t("n-selected", "{{count}} selected", {
                  count: selectedCategories.length
                })}
                size="small"
                onDelete={() => setSelectedCategories([])}
              />
            )}
            <ExportData
              data={sortedList}
              columns={exportCategoryColumns}
              filename={`categories_${new Date().toISOString().split("T")[0]}`}
              title={t("export-category-list", "Export category list")}
              buttonVariant="outlined"
              buttonText={t("export", "Export")}
              formats={["csv", "xlsx", "pdf", "json"]}
              onExportComplete={(format) => {
                sendAppLog(
                  LOG_LEVEL.INFO,
                  `Exported ${sortedList.length} categories as ${format}`,
                  buildLogContext(
                    "category",
                    "export-complete",
                    userDetails?.id,
                    userDetails?.companyId,
                    userDetails?.storeId,
                    {
                      categoryCount: sortedList.length,
                      format
                    }
                  )
                );
              }}
              onExportError={(error) => {
                console.error("Export failed:", error);
              }}
            />
          </CategoryListToolbar>
        </Box>

        <Popover
          open={filtersOpen}
          anchorEl={filtersAnchorEl}
          onClose={closeFiltersPopover}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                mt: 1.5,
                minWidth: 320,
                maxWidth: 400,
                maxHeight: "85vh",
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                boxShadow: "none"
              }
            }
          }}
        >
          <CategoryListFilters
            search={search}
            onSearchChange={setSearch}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            depthFilter={depthFilter}
            onDepthFilterChange={setDepthFilter}
            hasProductsFilter={hasProductsFilter}
            onHasProductsFilterChange={setHasProductsFilter}
            uniqueDepths={uniqueDepths}
            onClear={clearFilters}
            activeCount={activeFiltersCount}
          />
        </Popover>

        <Box component={motion.div} variants={itemVariants}>
          <CategoryListTable
            categoryTree={categoryTree}
            filteredCount={filteredList.length}
            selectedIds={selectedCategories}
            order={order}
            orderBy={orderBy}
            onSort={handleSort}
            onSelectAll={handleSelectAll}
            onSelectOne={handleSelectOne}
            onEdit={(cat) => {
              setSelectedCategory(cat);
              setOpenEditModal(true);
            }}
            onDelete={(cat) => {
              setSelectedCategory(cat);
              setOpenDeleteModal(true);
            }}
            onView={(cat) => {
              setSelectedCategoryForView(cat);
              setSelectedParentCategory(null);
              setViewMode("category-detail");
            }}
            isMobile={matchMD}
          />
        </Box>

        <Box
          component={motion.div}
          variants={itemVariants}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="body2" color="text.secondary">
            {t(
              "showing-x-of-y-categories",
              "Showing {{filtered}} of {{total}} categories",
              { filtered: filteredList.length, total: categoryList.length }
            )}
          </Typography>
          {selectedCategories.length > 0 && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => {
                  sendAppLog(
                    LOG_LEVEL.INFO,
                    "Bulk delete initiated",
                    buildLogContext(
                      "category",
                      "bulk-delete",
                      userDetails?.id,
                      userDetails?.companyId,
                      userDetails?.storeId,
                      {
                        selectedCategoryCount: selectedCategories.length
                      }
                    )
                  );
                }}
                sx={{ textTransform: "none" }}
              >
                {t("delete-selected", "Delete selected")}
              </Button>
            </Stack>
          )}
        </Box>
      </Box>

      <AddCategoryModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSuccess={refetchCategories}
      />
      {selectedCategory && (
        <>
          <EditCategoryModal
            open={openEditModal}
            onClose={() => {
              setOpenEditModal(false);
              setSelectedCategory(null);
            }}
            category={selectedCategory}
            onSuccess={refetchCategories}
          />
          <DeleteCategoryModal
            open={openDeleteModal}
            onClose={() => {
              setOpenDeleteModal(false);
              setSelectedCategory(null);
            }}
            category={selectedCategory}
          />
        </>
      )}
    </Box>
  );
};

export default CategoryList;
